import React, { ReactElement, useEffect, useState } from 'react';
import {
  ChannelStatusAction,
  ChannelStatusUpdateInput,
  ClinicSettingsConversationsChannelStatusFragment as Status,
  useDeleteClinicSettingsConversationsChannelStatusMutation as useDeleteStatus,
  useUpdateClinicSettingsConversationsChannelStatusSortOrderMutation as useUpdateStatusOrder,
} from 'shared/types/graphql';
import partial from 'lodash-es/partial';
import useClinicUser from 'shared/hooks/useClinicUser';
import { Reference } from '@apollo/client';
import { useToast } from '@televet/kibble-ui/build/components/Toast';
import { SortableItem, SortableList } from '@televet/kibble-ui/build/components/SortableList';
import { Box } from '@televet/kibble-ui/build/chakra';
import StatusListItem from './StatusListItem';
import { useTracking } from 'shared/providers/TrackingProvider';
import { SettingsConversationEvents } from 'shared/providers/TrackingProvider/EventNames';

interface Props {
  statuses: Status[];
  filter: string;
  handleChannelStatusUpdate: (channelStatusId: string, data: ChannelStatusUpdateInput) => void;
  statusListLoading: (statusType: string, loading: boolean) => void;
  lastAdded: string;
  setLastAdded: (lastAdded: string) => void;
  statusType: string;
  showExport?: boolean;
}

export const StatusList = ({
  statuses: initialStatuses,
  filter,
  handleChannelStatusUpdate,
  statusListLoading,
  lastAdded,
  setLastAdded,
  statusType,
}: Props): ReactElement => {
  const [statuses, setStatuses] = useState<Status[]>([]);
  useEffect(() => setStatuses(initialStatuses), [initialStatuses]);
  const { trackEvent } = useTracking();

  const [updateStatusOrder] = useUpdateStatusOrder({
    onCompleted: () => toast({ id: 'SortToast', title: 'Statuses Order Saved', status: 'success' }),
  });

  const { clinicUser } = useClinicUser();
  const toast = useToast();

  const [deleteChannelStatus] = useDeleteStatus({
    update: (cache, mutationResult): void => {
      cache.modify({
        fields: {
          findManyChannelStatus(existingChannelStatuses = []): Status[] {
            return existingChannelStatuses.filter(
              (status: Reference) =>
                status?.__ref !== 'ChannelStatus:' + mutationResult.data?.updateOneChannelStatus.id,
            );
          },
        },
      });
    },
  });

  const handleStatusDelete = (status: Status): void => {
    const newList = statuses.filter((item) => item.id !== status.id);

    setStatuses(newList);
    statusListLoading(statusType, true);

    //delete
    deleteChannelStatus({
      variables: {
        channelStatusId: status.id,
        data: {
          deletedOn: new Date().toISOString(),
          deletedBy: { connect: { id: clinicUser?.id } },
        },
      },
    });
  };

  // before sending a mutation, use this map to set the correct order.
  const ActionOrderPaddings: Record<ChannelStatusAction, number> = {
    Active: 1,
    Inactive: 101,
    InactivePermanently: 202,
    Migrated: 302,
  };

  const handleListSort = (statuses: Status[]): void => {
    setStatuses(statuses);
    const data = statuses.map((status, index) => {
      return {
        id: status.id,
        order: index + ActionOrderPaddings[status.channelStatusAction],
      };
    });
    updateStatusOrder({ variables: { data } });
    trackEvent(SettingsConversationEvents.UpdateStatusOrder, { StatusType: statusType });
  };

  return (
    <Box mt={5}>
      <SortableList
        items={statuses}
        onChange={(statuses): void => handleListSort(statuses)}
        renderItem={(status, index): JSX.Element => (
          <SortableItem id={status.id}>
            <StatusListItem
              handleChannelStatusUpdate={handleChannelStatusUpdate}
              id={status.id}
              status={status}
              index={index}
              filter={filter}
              lastAdded={lastAdded}
              onDelete={partial(handleStatusDelete, status)}
              setLastAdded={setLastAdded}
              statusesLength={statuses.length}
              statusType={statusType}
            />
          </SortableItem>
        )}
      />
    </Box>
  );
};
